import { Dispatch, Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ActivityRun } from '@robocorp/robocloud-orchestrator-interface';
import { activityRunService } from '../services/runAPI';
import { OkAction, ErrorAction } from './baseActions';
import {
  ActivityRunEvent,
  ActivityRunConsoleMessage,
  ActivityRunSocketInfo,
} from '../types/resources';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum ActivityRunActionType {
  SILENT_GET_ACTIVITY_RUN_CONSOLE = 'SILENT_GET_ACTIVITY_RUN_CONSOLE',
  GET_ACTIVITY_RUN_CONSOLE = 'GET_ACTIVITY_RUN_CONSOLE',
  GET_ACTIVITY_RUN_CONSOLE_OK = 'GET_ACTIVITY_RUN_CONSOLE_OK',
  GET_ACTIVITY_RUN_CONSOLE_ERROR = 'GET_ACTIVITY_RUN_CONSOLE_ERROR',
  SILENT_GET_ACTIVITY_RUN_EVENTS = 'SILENT_GET_ACTIVITY_RUN_EVENTS',
  GET_ACTIVITY_RUN_EVENTS = 'GET_ACTIVITY_RUN_EVENTS',
  GET_ACTIVITY_RUN_EVENTS_OK = 'GET_ACTIVITY_RUN_EVENTS_OK',
  GET_ACTIVITY_RUN_EVENTS_ERROR = 'GET_ACTIVITY_RUN_EVENTS_ERROR',
  SET_ACTIVITY_RUN_CONSOLE_MESSAGE_EVENT = 'SET_ACTIVITY_RUN_CONSOLE_MESSAGE_EVENT',
  SET_ACTIVITY_RUN_EVENT = 'SET_ACTIVITY_RUN_EVENT',
  SET_ACTIVITY_RUN = 'SET_ACTIVITY_RUN',
  GET_ACTIVITY_RUN_SOCKET_INFO = 'GET_ACTIVITY_RUN_SOCKET_INFO',
  GET_ACTIVITY_RUN_SOCKET_INFO_OK = 'GET_ACTIVITY_RUN_SOCKET_INFO_OK',
  GET_ACTIVITY_RUN_SOCKET_INFO_ERROR = 'GET_ACTIVITY_RUN_SOCKET_INFO_ERROR',
  SILENT_GET_ACTIVITY_RUN = 'SILENT_GET_ACTIVITY_RUN',
  GET_ACTIVITY_RUN = 'GET_ACTIVITY_RUN',
  GET_ACTIVITY_RUN_OK = 'GET_ACTIVITY_RUN_OK',
  GET_ACTIVITY_RUN_ERROR = 'GET_ACTIVITY_RUN_ERROR',
  GET_ACTIVITY_RUNS = 'GET_ACTIVITY_RUNS',
  GET_ACTIVITY_RUNS_OK = 'GET_ACTIVITY_RUNS_OK',
  GET_ACTIVITY_RUNS_ERROR = 'GET_ACTIVITY_RUNS_ERROR',
}

type WithActivityRunId = { activityRunId: string };

type GetActivityRunSocketInfoAction =
  | (Action<ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO> & WithActivityRunId)
  | (OkAction<ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO_OK, ActivityRunSocketInfo> &
      WithActivityRunId)
  | (ErrorAction<ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO_ERROR> & WithActivityRunId);

type GetActivityRunEventsAction =
  | Action<ActivityRunActionType.SILENT_GET_ACTIVITY_RUN_EVENTS>
  | (Action<ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS> & WithActivityRunId)
  | (OkAction<ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS_OK, ActivityRunEvent[]> &
      WithActivityRunId)
  | (ErrorAction<ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS_ERROR> & WithActivityRunId);

type GetActivityRunConsoleMessagesAction =
  | Action<ActivityRunActionType.SILENT_GET_ACTIVITY_RUN_CONSOLE>
  | (Action<ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE> & WithActivityRunId)
  | (OkAction<ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE_OK, ActivityRunConsoleMessage[]> &
      WithActivityRunId)
  | (ErrorAction<ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE_ERROR> & WithActivityRunId);

type GetActivityRunAction =
  | Action<ActivityRunActionType.SILENT_GET_ACTIVITY_RUN>
  | (Action<ActivityRunActionType.GET_ACTIVITY_RUN> & WithActivityRunId)
  | (OkAction<ActivityRunActionType.GET_ACTIVITY_RUN_OK, ActivityRun> & WithActivityRunId)
  | (ErrorAction<ActivityRunActionType.GET_ACTIVITY_RUN_ERROR> & WithActivityRunId);

type GetActivityRuns =
  | Action<ActivityRunActionType.GET_ACTIVITY_RUNS>
  | OkAction<ActivityRunActionType.GET_ACTIVITY_RUNS_OK, ActivityRun[]>
  | ErrorAction<ActivityRunActionType.GET_ACTIVITY_RUNS_ERROR>;

type SetActivityRunConsoleMessageAction =
  Action<ActivityRunActionType.SET_ACTIVITY_RUN_CONSOLE_MESSAGE_EVENT> & {
    event: ActivityRunConsoleMessage;
  };
type SetActivityRunEventAction = Action<ActivityRunActionType.SET_ACTIVITY_RUN_EVENT> & {
  events: ActivityRunEvent[];
};
type SetActivityRunAction = Action<ActivityRunActionType.SET_ACTIVITY_RUN> & {
  activityRun: ActivityRun;
};

export type ActivityRunAction =
  | GetActivityRunEventsAction
  | GetActivityRunConsoleMessagesAction
  | SetActivityRunConsoleMessageAction
  | SetActivityRunEventAction
  | SetActivityRunAction
  | GetActivityRunAction
  | GetActivityRuns
  | GetActivityRunSocketInfoAction;

export const setActivityRunConsoleMessageEvent =
  (event: ActivityRunConsoleMessage) =>
  (dispatch: Dispatch<SetActivityRunConsoleMessageAction>) => {
    dispatch({
      type: ActivityRunActionType.SET_ACTIVITY_RUN_CONSOLE_MESSAGE_EVENT,
      event,
    });
  };

export const setActivityRunEvent =
  (events: ActivityRunEvent[]) => (dispatch: Dispatch<SetActivityRunEventAction>) => {
    dispatch({ type: ActivityRunActionType.SET_ACTIVITY_RUN_EVENT, events });
  };

export const setActivityRun =
  (activityRun: ActivityRun) => (dispatch: Dispatch<SetActivityRunAction>) => {
    dispatch({ type: ActivityRunActionType.SET_ACTIVITY_RUN, activityRun });
  };

export const getActivityRunsByActivity =
  (processId: string, activityId: string): ThunkAction<void, AppState, any, GetActivityRuns> =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityRunActionType.GET_ACTIVITY_RUNS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunsByActivity({
      workspaceId,
      processId,
      activityId,
    });

    if (response.ok) {
      dispatch({
        type: ActivityRunActionType.GET_ACTIVITY_RUNS_OK,
        data: response.data as ActivityRun[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUNS_ERROR,
      });
    }
  };

export const getActivityRunsByProcessRun =
  (processId: string, processRunId: string): ThunkAction<void, AppState, any, GetActivityRuns> =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityRunActionType.GET_ACTIVITY_RUNS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunsByProcessRun({
      workspaceId,
      processId,
      processRunId,
    });

    if (response.ok) {
      dispatch({
        type: ActivityRunActionType.GET_ACTIVITY_RUNS_OK,
        data: response.data as ActivityRun[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUNS_ERROR,
      });
    }
  };

export const getActivityRun =
  (
    processId: string,
    processRunId: string,
    activityRunId: string,
  ): ThunkAction<void, AppState, any, GetActivityRunAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activityRuns.byId[activityRunId]) {
      dispatch({ type: ActivityRunActionType.SILENT_GET_ACTIVITY_RUN });
    } else {
      dispatch({ type: ActivityRunActionType.GET_ACTIVITY_RUN, activityRunId });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRun({
      workspaceId,
      processId,
      processRunId,
      activityRunId,
    });

    if (response.ok) {
      dispatch({
        activityRunId,
        type: ActivityRunActionType.GET_ACTIVITY_RUN_OK,
        data: response.data as ActivityRun,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUN_ERROR,
        activityRunId,
      });
    }
  };

export const getActivityRunWebSocketInfo =
  (
    processId: string,
    processRunId: string,
    activityRunId: string,
  ): ThunkAction<void, AppState, any, GetActivityRunSocketInfoAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO,
      activityRunId,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunSocketInfo({
      workspaceId,
      processId,
      processRunId,
      activityRunId,
    });

    if (response.ok) {
      dispatch({
        activityRunId,
        type: ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO_OK,
        data: response.data as ActivityRunSocketInfo,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUN_SOCKET_INFO_ERROR,
        activityRunId,
      });
    }
  };

export const getActivityRunConsoleMessages =
  (
    processId: string,
    processRunId: string,
    activityRunId: string,
  ): ThunkAction<void, AppState, any, GetActivityRunConsoleMessagesAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activityRunConsoleMessages.byActivityRunId[activityRunId]) {
      dispatch({ type: ActivityRunActionType.SILENT_GET_ACTIVITY_RUN_CONSOLE });
    } else {
      dispatch({
        type: ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE,
        activityRunId,
      });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunConsoleMessages({
      workspaceId,
      processId,
      processRunId,
      activityRunId,
    });

    if (response.ok) {
      dispatch({
        activityRunId,
        type: ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE_OK,
        data: response.data as ActivityRunConsoleMessage[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUN_CONSOLE_ERROR,
        activityRunId,
      });
    }
  };

export const getActivityRunEvents =
  (
    processId: string,
    processRunId: string,
    activityRunId: string,
  ): ThunkAction<void, AppState, any, GetActivityRunEventsAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activityRunEvents.byActivityRunId[activityRunId]) {
      dispatch({ type: ActivityRunActionType.SILENT_GET_ACTIVITY_RUN_EVENTS });
    } else {
      dispatch({
        type: ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS,
        activityRunId,
      });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityRunService.getActivityRunEvents({
      workspaceId,
      processId,
      processRunId,
      activityRunId,
    });

    if (response.ok) {
      dispatch({
        activityRunId,
        type: ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS_OK,
        data: response.data as ActivityRunEvent[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityRunActionType.GET_ACTIVITY_RUN_EVENTS_ERROR,
        activityRunId,
      });
    }
  };
